import { CTAButton } from 'component/button';
import { colorBorderPrimary, colorSurfaceSecondary, colorTextSecondary } from 'component/color';
import { RichText } from 'component/richtext';
import { AutoSizeTextInput, UtilityText } from 'component/text';
import { BlocklistAddTermField, BlocklistItemList, BlocklistSearchField } from 'open-modules/zdf/component/blocklist/blocklist';
import {
    BlocklistAddTermModal,
    BlocklistDeleteModal,
    BlocklistEditModal,
    BlocklistHelpModal,
    BlocklistItemEditDeleteMenu,
} from 'open-modules/zdf/component/blocklist/blocklistdialogs';
import { BlocklistTextInputWithChips } from 'open-modules/zdf/component/blocklist/blocklistTextInput';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native-web';
import { CLICK, component, DemoSection, demoFile, INPUT, POPUP, story, WITHIN } from 'system/demo';
import { t } from 'util/stdtypes';

// Minimal server call mocks used by the widget
const serverCall = {
    blocklist: {
        addToBlocklist: async () => Promise.resolve(true),
        removeFromBlocklist: async () => Promise.resolve(true),
        RemoveFromBlocklist: async () => Promise.resolve(true),
        getCharacterReplacements: async () => ({}),
        getBlocklist: async () => Promise.resolve(true),
        findTermInBlocklist: async () => Promise.resolve(false),
    },
};

const sampleEntries = [
    { term: 'Lorem Ipsum1', isSingularTerm: true, hasAlternativeSpellings: false },
    { term: 'Lorem Ipsum2', isSingularTerm: false, hasAlternativeSpellings: true },
];

const BlocklistLogStyle = StyleSheet.create({
    logBox: {
        backgroundColor: colorSurfaceSecondary,
        padding: 6,
        borderColor: colorBorderPrimary,
        borderWidth: 1,
    },
});

demoFile('open-modules/zdf/component/blocklist/blocklist');

component(
    'BlocklistAddTermField',
    BlocklistAddTermField,
    {
        state: { serverCall },
    },
    [
        story('Enter terms separated by semicolons and save', [
            INPUT('blocklistChipField', 'term1; term2'),
            CLICK('Add term'),
            POPUP(CLICK('Save')),
        ]),
        story('Enter single term and click add', [INPUT('blocklistChipField', 'term1'), CLICK('Add term'), POPUP(CLICK('Save'))]),
    ],
);

component('BlocklistSearchField', BlocklistSearchField);

component(
    'BlocklistItemList',
    BlocklistItemListDemo,
    {
        state: {
            roles: ['Super-Moderator'],
            serverCall: {
                blocklist: {
                    getCharacterReplacements: async () => ({}),
                },
            },
        },
    },
    [story('Select first item', [WITHIN('item-Lorem Ipsum1', CLICK('checkbox-Lorem Ipsum1'))])],
);

component('BlocklistDemo', BlocklistDemo, {
    state: {
        roles: ['Super-Moderator'],
        serverCall,
    },
});

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function BlocklistDemo() {
    const [blocklist, setBlocklist] = useState({
        someId: { term: 'bad word', isSingularTerm: true, hasAlternativeSpellings: false },
    });
    const [text, setText] = useState('');
    const [selectedTerms, setSelectedTerms] = useState<string[]>([]);
    const [hasViolation, setHasViolation] = useState<boolean | null>(null);
    const s = BlocklistLogStyle;

    return (
        <DemoSection text='blocklist Management'>
            <DemoSection text='Singular term detection'>
                <View>
                    <RichText text={`Term: **${Object.values(blocklist)[0].term}**`} />
                    <AutoSizeTextInput
                        ariaLabel={t('!term input')}
                        onChange={(text) =>
                            setBlocklist({
                                someId: {
                                    term: text,
                                    isSingularTerm: true,
                                    hasAlternativeSpellings: false,
                                },
                            })
                        }
                        placeholderLabel={t('!Enter a new blocklisted term')}
                        style={{ border: '1px solid ' + colorBorderPrimary }}
                        testID='term-input'
                        value={Object.values(blocklist)[0].term}
                    />
                </View>

                <View>
                    <UtilityText text='Blocklist:' />
                    <View style={s.logBox}>
                        <UtilityText color={colorTextSecondary} text={JSON.stringify(blocklist)} type='tiny' weight='medium' />
                    </View>
                </View>

                <View>
                    <UtilityText text='Text:' />
                    <AutoSizeTextInput
                        ariaLabel={t('!text input')}
                        onChange={(t) => setText(t)}
                        placeholderLabel={t('!Enter a text to check against the blocklist')}
                        style={{ border: '1px solid ' + colorBorderPrimary }}
                        testID='check-text'
                        value={text}
                    />
                </View>

                <CTAButton onPress={() => setHasViolation(text.includes('bad word'))} text='Check text' />
                {hasViolation !== null && <UtilityText text={'Violation found: ' + hasViolation} />}
            </DemoSection>

            <DemoSection text='Add Term'>
                <BlocklistAddTermField />
            </DemoSection>

            <DemoSection text='Search'>
                <BlocklistSearchField />
            </DemoSection>

            <DemoSection text='blocklist Items'>
                <BlocklistItemList blocklistEntries={sampleEntries} selectedTerms={selectedTerms} setSelectedTerms={setSelectedTerms} />
            </DemoSection>
        </DemoSection>
    );
}

function BlocklistItemListDemo() {
    const [selectedTerms, setSelectedTerms] = useState<string[]>([]);
    return <BlocklistItemList blocklistEntries={sampleEntries} selectedTerms={selectedTerms} setSelectedTerms={setSelectedTerms} />;
}

function BlocklistAddTermModalDemo() {
    const [open, setOpen] = useState(false);
    return (
        <>
            <CTAButton onPress={() => setOpen(true)} text='Open Modal' />
            {open && <BlocklistAddTermModal onClose={() => setOpen(false)} onSave={() => {}} terms={['term1']} />}
        </>
    );
}

function BlocklistEditModalDemo() {
    const [open, setOpen] = useState(false);
    return (
        <>
            <CTAButton onPress={() => setOpen(true)} text='Open Modal' />
            {open && (
                <BlocklistEditModal hasAlternativeSpellings={false} isSingularTerm={true} onClose={() => setOpen(false)} term='term1' />
            )}
        </>
    );
}

function BlocklistDeleteModalDemo() {
    const [open, setOpen] = useState(false);
    return (
        <>
            <CTAButton onPress={() => setOpen(true)} text='Open Modal' />
            {open && <BlocklistDeleteModal onClose={() => setOpen(false)} termsToDelete={['term1']} />}
        </>
    );
}

function BlocklistHelpModalDemo() {
    const [open, setOpen] = useState(false);
    return (
        <>
            <CTAButton onPress={() => setOpen(true)} text='Open Modal' />
            {open && <BlocklistHelpModal onClose={() => setOpen(false)} />}
        </>
    );
}

function BlocklistItemEditDeleteMenuDemo() {
    return <BlocklistItemEditDeleteMenu onDeletePressed={() => {}} onEditPressed={() => {}} />;
}

component('BlocklistAddTermModal', BlocklistAddTermModalDemo, { state: { serverCall } }, [
    story('Save term', [CLICK('Open Modal'), POPUP(CLICK('Save'))]),
]);

component('BlocklistEditModal', BlocklistEditModalDemo, { state: { serverCall } }, [
    story('Save edit', [CLICK('Open Modal'), POPUP(CLICK('Save'))]),
]);

component('BlocklistDeleteModal', BlocklistDeleteModalDemo, { state: { serverCall } }, [
    story('Confirm delete', [CLICK('Open Modal'), POPUP(CLICK('Delete'))]),
]);

component('BlocklistHelpModal', BlocklistHelpModalDemo, {}, [story('Close help', [CLICK('Open Modal'), POPUP(CLICK('All right'))])]);

component('BlocklistItemEditDeleteMenu', BlocklistItemEditDeleteMenuDemo, {}, [story('Click options', [CLICK('Edit'), CLICK('Delete')])]);
